import React from 'react';
import { NoSsr, Typography } from '@mui/material';
import Link from '@/components/atoms/Link/Link';

import { useTranslation } from '@/src/i18n';
import { APP_ROUTES } from '@/src/constants';
import NotFoundPage from '@/components/templates/404/NotFoundPage';
import ErrorPage from '@/components/templates/ErrorPage/ErrorPage';

type Props = {
  statusCode: number;
};

// NoSsr is used because on server side it returns the normal header
// so we can have conflicts during the hydration without it
const Error = ({ statusCode }: Props) => {
  const { t } = useTranslation();

  return (
    <NoSsr>
      <div className="relative flex flex-row">
        <div
          data-testid="main-header"
          className="fixed top-0 z-[1250] h-14 w-full bg-surface-brand px-4 lg:px-6"
        >
          <div className="relative flex h-full items-center ">
            <div className="flex flex-1 items-center justify-between">
              <div className="flex flex-row items-center gap-x-2">
                <Link href={APP_ROUTES.HOME} passHref legacyBehavior>
                  <a
                    data-cy="header-logo"
                    title={t('back-to-maiia-home')}
                    aria-label={t('back-to-maiia-home')}
                  >
                    <img
                      src="/static/images/logo-maiia-monochrome.svg"
                      className="h-8 cursor-pointer"
                      alt="Logo Maiia"
                    />
                  </a>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      {statusCode === 404 ? (
        <NotFoundPage />
      ) : (
        <ErrorPage
          message={<Typography mt={2}>{t('text_page_error')}</Typography>}
        />
      )}
    </NoSsr>
  );
};

// Error does not currently support Next.js Data Fetching methods like getStaticProps or getServerSideProps.
// https://nextjs.org/docs/pages/building-your-application/routing/custom-error#caveats
Error.getInitialProps = ({ res, err }) => {
  let statusCode = 404;
  if (res) {
    // Custom 404, because we not respone 404 statusCode, see file server/index.js
    if (res.pageNotFound) statusCode = 404;
    // eslint-disable-next-line prefer-destructuring
    else if (res.statusCode) statusCode = res.statusCode;
  }
  // eslint-disable-next-line prefer-destructuring
  else if (err) statusCode = err.statusCode;
  const headTitle =
    statusCode === 404 ? 'common:page_not_found' : 'common:page_error';

  return { statusCode, headTitle, namespacesRequired: ['common'] };
};

export default Error;
